import { AnimatePresence, motion } from 'framer-motion'
import { X, Home, Calendar, Target, FileText, History, BookOpen, TrendingUp, MessageSquare, Bell, Trophy, User, CreditCard, HelpCircle, Brain, LogOut } from 'lucide-react'

interface StudentMobileMenuProps {
    isOpen: boolean
    onClose: () => void
    activeTab: string
    onNavigate: (tab: string) => void
}

export function StudentMobileMenu({ isOpen, onClose, activeTab, onNavigate }: StudentMobileMenuProps) {
    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: Home },
        { id: 'daily-plan', label: 'Daily Plan', icon: Calendar },
        { id: 'discipline', label: 'Discipline Learning', icon: Target },
        { id: 'tests', label: 'Tests', icon: FileText },
        { id: 'test-history', label: 'Test History', icon: History },
        { id: 'study-material', label: 'Study Material', icon: BookOpen },
        { id: 'question-bank', label: 'Question Bank', icon: Brain },
        { id: 'performance', label: 'Performance Community', icon: TrendingUp },
        { id: 'chat', label: 'Chat with Mentor', icon: MessageSquare },
        { id: 'announcements', label: 'Announcements', icon: Bell },
        { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
        { id: 'profile', label: 'Account', icon: User },
        { id: 'payments', label: 'Payments', icon: CreditCard },
        { id: 'support', label: 'Support', icon: HelpCircle },
    ]

    const handleItemClick = (itemId: string) => {
        onNavigate(itemId)
        onClose()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden"
                    />

                    {/* Menu Drawer */}
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl z-50 lg:hidden overflow-y-auto"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">AS</span>
                                </div>
                                <div>
                                    <h2 className="font-bold text-theme-primary">Arun</h2>
                                    <p className="text-xs text-theme-secondary">Student Portal</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <X className="h-5 w-5 text-theme-secondary" />
                            </button>
                        </div>

                        {/* Menu Items */}
                        <div className="p-3 space-y-1">
                            {menuItems.map((item) => {
                                const Icon = item.icon
                                const isActive = activeTab === item.id

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive
                                            ? 'bg-gradient-to-r from-primary-blue to-primary-purple text-white shadow-lg'
                                            : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-theme-primary'
                                            }`}
                                    >
                                        <Icon className={`h-5 w-5 ${isActive ? 'text-white' : ''}`} />
                                        <span className={`font-medium text-sm ${isActive ? 'text-white' : ''}`}>
                                            {item.label}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-3 border-t border-slate-200 dark:border-slate-800 mt-auto">
                            <button
                                onClick={() => window.location.href = '/'}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                                <LogOut className="h-5 w-5" />
                                <span className="font-medium text-sm">Logout</span>
                            </button>
                            <p className="text-xs text-theme-secondary text-center mt-4">
                                © 2024 Future Founder. All rights reserved.
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
